<script>
import Title from '@/components/admin/common/TitleArea.vue'

export default {
  name: 'PartnerOrderReceiving',
  components: { Title },
  data() {
    return {
      description: { title: '파트너 오더 입고처리' },
      waybillNo: '',
      processing: false,
      allowPackingOwnerOverride: false,
      lastResult: null,
      scanHistory: []
    }
  },
  methods: {
    focusScanner() {
      this.$nextTick(() => this.$refs.scannerInput?.focus())
    },
    resultMessage(error) {
      return error?.response?.data?.message || '입고 처리에 실패했습니다.'
    },
    prependHistory(entry) {
      this.scanHistory.unshift({
        ...entry,
        scannedAt: new Date().toLocaleTimeString('ko-KR')
      })
      this.scanHistory = this.scanHistory.slice(0, 20)
    },
    requestPackingOwnerOverride(enabled) {
      if (!enabled) {
        this.allowPackingOwnerOverride = false
        this.focusScanner()
        return
      }

      this.$q.dialog({
        title: '관리자 예외 입고를 활성화하시겠습니까?',
        message: '활성화하면 포장주체가 AONE인 주문도 입고 처리되며, 주문 진행상태와 포장상태가 즉시 완료 상태로 변경됩니다. 장애 또는 AONE 포장 불가 상황에서만 사용해 주세요.',
        persistent: true,
        ok: { label: '확인 후 활성화', color: 'negative', unelevated: true },
        cancel: { label: '취소', flat: true }
      }).onOk(() => {
        this.allowPackingOwnerOverride = true
        this.focusScanner()
      }).onCancel(() => {
        this.allowPackingOwnerOverride = false
        this.focusScanner()
      })
    },
    async receive() {
      const scannedWaybillNo = String(this.waybillNo || '').trim()
      if (!scannedWaybillNo || this.processing) {
        this.focusScanner()
        return
      }

      this.processing = true
      this.lastResult = null
      try {
        const response = await this.$http({
          method: 'POST',
          url: '/api/fulfillment/admin/partner-order-receiving',
          data: {
            waybillNo: scannedWaybillNo,
            allowPackingOwnerOverride: this.allowPackingOwnerOverride
          },
          name: '[ 파트너 오더 / 입고처리 ]'
        })
        const data = response.data?.data || {}
        const message = response.data?.message || '입고 처리가 완료되었습니다.'
        this.lastResult = { ok: true, message, data }
        this.prependHistory({ ok: true, waybillNo: scannedWaybillNo, message, data })
        this.$q.notify({ type: data.alreadyReceived ? 'info' : 'positive', message })
        this.waybillNo = ''
      } catch (error) {
        const message = this.resultMessage(error)
        this.lastResult = { ok: false, message, waybillNo: scannedWaybillNo }
        this.prependHistory({ ok: false, waybillNo: scannedWaybillNo, message })
        this.$q.notify({ type: 'negative', message })
        this.waybillNo = ''
      } finally {
        this.processing = false
        this.focusScanner()
      }
    }
  },
  mounted() {
    this.focusScanner()
  },
  activated() {
    this.focusScanner()
  },
  deactivated() {
    // 위험 옵션이 KeepAlive 화면 전환 뒤에도 남지 않도록 기본값으로 복원한다.
    this.allowPackingOwnerOverride = false
  }
}
</script>

<template>
  <q-page class="receiving-page q-pa-lg" @click="focusScanner">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description" />
      </div>
    </div>

    <div class="receiving-layout">
      <section class="scan-panel comp-box q-pa-xl">
        <div class="scan-kicker">BARCODE RECEIVING</div>
        <h1 class="scan-title">운송장 바코드를 스캔하세요</h1>
        <p class="scan-description">
          기본적으로 파트너가 포장한 주문만 처리됩니다. 스캔이 완료되면 진행상태는 <strong>출발지-반입(03)</strong>,
          포장상태는 <strong>COMPLETED</strong>로 함께 변경됩니다.
        </p>

        <div class="override-control q-mb-lg" :class="{ 'override-control--active': allowPackingOwnerOverride }">
          <div class="override-control__copy">
            <div class="row items-center q-gutter-sm">
              <q-icon name="warning_amber" :color="allowPackingOwnerOverride ? 'negative' : 'grey-7'" size="22px" />
              <strong>관리자 예외 입고</strong>
              <q-badge
                :color="allowPackingOwnerOverride ? 'negative' : 'grey-5'"
                :label="allowPackingOwnerOverride ? '활성' : '비활성'"
              />
            </div>
            <div class="override-control__description">
              활성화하면 포장주체와 관계없이 입고 처리합니다. 예외 처리는 감사 이력에 기록됩니다.
            </div>
          </div>
          <q-toggle
            :model-value="allowPackingOwnerOverride"
            color="negative"
            :disable="processing"
            aria-label="관리자 예외 입고"
            @update:model-value="requestPackingOwnerOverride"
          />
        </div>

        <q-form class="scan-form" @submit.prevent="receive">
          <q-input
            ref="scannerInput"
            v-model="waybillNo"
            outlined
            autofocus
            autocomplete="off"
            inputmode="none"
            maxlength="100"
            :disable="processing"
            placeholder="운송장번호 스캔 또는 직접 입력"
            aria-label="운송장번호"
            class="scanner-input"
          >
            <template #prepend>
              <q-icon name="qr_code_scanner" color="primary" />
            </template>
            <template #append>
              <q-spinner v-if="processing" color="primary" size="24px" />
              <q-icon v-else-if="waybillNo" name="close" class="cursor-pointer" @click.stop="waybillNo = ''" />
            </template>
          </q-input>
          <q-btn
            type="submit"
            color="primary"
            unelevated
            icon="inventory"
            label="입고 처리"
            :loading="processing"
            :disable="!waybillNo.trim()"
            class="receive-button"
          />
        </q-form>

        <div
          v-if="lastResult"
          class="result-card q-mt-lg"
          :class="lastResult.ok ? 'result-success' : 'result-error'"
          role="status"
          aria-live="polite"
        >
          <q-icon :name="lastResult.ok ? 'check_circle' : 'error'" size="32px" />
          <div>
            <div class="result-title">{{ lastResult.message }}</div>
            <div v-if="lastResult.ok" class="result-detail">
              {{ lastResult.data.hblNo }}
              <span v-if="lastResult.data.partnerName"> · {{ lastResult.data.partnerName }}</span>
            </div>
            <div v-else class="result-detail">{{ lastResult.waybillNo }}</div>
          </div>
        </div>

        <div class="scan-hint q-mt-lg">
          <q-icon name="keyboard_return" size="18px" />
          대부분의 바코드 스캐너는 스캔 후 Enter를 자동 입력합니다.
        </div>
      </section>

      <aside class="history-panel comp-box">
        <div class="history-header q-pa-md">
          <div>
            <div class="text-subtitle1 text-weight-bold">최근 스캔</div>
            <div class="text-caption text-grey-7">현재 화면에서 처리한 최근 20건</div>
          </div>
          <q-badge color="grey-3" text-color="grey-9" :label="scanHistory.length" />
        </div>
        <q-separator />

        <div v-if="!scanHistory.length" class="empty-history">
          <q-icon name="qr_code_scanner" size="42px" color="grey-5" />
          <div>아직 스캔 내역이 없습니다.</div>
        </div>
        <q-list v-else separator>
          <q-item v-for="(item, index) in scanHistory" :key="`${item.scannedAt}-${index}`" class="q-py-md">
            <q-item-section avatar>
              <q-avatar :color="item.ok ? 'green-1' : 'red-1'" :text-color="item.ok ? 'green-8' : 'red-8'" size="36px">
                <q-icon :name="item.ok ? 'check' : 'close'" />
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-weight-medium">{{ item.waybillNo }}</q-item-label>
              <q-item-label caption lines="2">{{ item.message }}</q-item-label>
            </q-item-section>
            <q-item-section side top>
              <q-item-label caption>{{ item.scannedAt }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </aside>
    </div>
  </q-page>
</template>

<style scoped>
.receiving-page {
  background: #f7f9fc;
  min-height: 100%;
}

.receiving-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(320px, 0.8fr);
  gap: 24px;
  align-items: start;
}

.scan-panel,
.history-panel {
  border: 1px solid #e4e9f1;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 28px rgba(30, 55, 90, 0.06);
}

.scan-panel {
  min-height: 470px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.scan-kicker {
  color: #2f6fed;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.18em;
}

.scan-title {
  margin: 10px 0 8px;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.2;
  letter-spacing: -0.04em;
}

.scan-description {
  max-width: 680px;
  margin: 0 0 30px;
  color: #5b6472;
  line-height: 1.7;
}

.scan-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px;
  gap: 12px;
}

.override-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  max-width: 680px;
  padding: 14px 16px;
  border: 1px solid #dfe4ec;
  border-radius: 12px;
  background: #f8fafc;
}

.override-control--active {
  border-color: #ef9a9a;
  background: #fff3f3;
}

.override-control__copy {
  min-width: 0;
}

.override-control__description {
  margin-top: 5px;
  color: #697386;
  font-size: 13px;
  line-height: 1.5;
}

.scanner-input :deep(.q-field__control) {
  height: 64px;
  border-radius: 12px;
  font-size: 20px;
}

.receive-button {
  min-height: 64px;
  border-radius: 12px;
  font-weight: 700;
}

.result-card {
  display: flex;
  gap: 12px;
  align-items: center;
  border-radius: 12px;
  padding: 16px 18px;
}

.result-success {
  color: #17633a;
  background: #eaf8f0;
  border: 1px solid #bfe8cf;
}

.result-error {
  color: #9a2c2c;
  background: #fff0f0;
  border: 1px solid #f2c4c4;
}

.result-title {
  font-size: 16px;
  font-weight: 700;
}

.result-detail,
.scan-hint {
  font-size: 13px;
  opacity: 0.82;
}

.scan-hint {
  display: flex;
  gap: 6px;
  align-items: center;
  color: #697386;
}

.history-panel {
  overflow: hidden;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.empty-history {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #8a93a3;
}

@media (max-width: 960px) {
  .receiving-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .receiving-page {
    padding: 16px;
  }

  .scan-panel {
    padding: 24px;
  }

  .scan-form {
    grid-template-columns: 1fr;
  }
}
</style>
